import cn from "classnames";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconChevronDown, IconSearch, IconX } from "./icons";
import { Input } from "./input";
import type { SelectOption } from "./select";

const ITEM_HEIGHT = 40;
const CONTAINER_HEIGHT = 256;
const OVERSCAN = 5;
const VIRTUALIZE_THRESHOLD = 50;

function highlightMatches(text: string, query: string): React.ReactNode {
  if (!query.trim()) {
    return text;
  }

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  let index = lowerText.indexOf(lowerQuery);
  while (index !== -1) {
    // Add text before the match
    if (index > lastIndex) {
      parts.push(text.slice(lastIndex, index));
    }
    // Add the matched text with highlighting
    parts.push(
      <mark
        key={`${index}-${lastIndex}`}
        className="bg-yellow-400 text-gray-900 font-medium p-1 rounded"
      >
        {text.slice(index, index + query.length)}
      </mark>,
    );
    lastIndex = index + query.length;
    index = lowerText.indexOf(lowerQuery, lastIndex);
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length > 0 ? parts : text;
}

function useVirtualList(itemCount: number) {
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollTop, setScrollTop] = useState(0);

  const handleScroll = useCallback(() => {
    if (scrollContainerRef.current) {
      setScrollTop(scrollContainerRef.current.scrollTop);
    }
  }, []);

  const totalHeight = itemCount * ITEM_HEIGHT;
  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / ITEM_HEIGHT) - OVERSCAN,
  );
  const visibleCount = Math.ceil(CONTAINER_HEIGHT / ITEM_HEIGHT) + 2 * OVERSCAN;
  const endIndex = Math.min(itemCount - 1, startIndex + visibleCount);

  const topPadding = startIndex * ITEM_HEIGHT;
  const bottomPadding = Math.max(0, (itemCount - endIndex - 1) * ITEM_HEIGHT);

  const virtualItems = useMemo(() => {
    const items: { index: number }[] = [];
    for (let i = startIndex; i <= endIndex; i++) {
      items.push({ index: i });
    }
    return items;
  }, [startIndex, endIndex]);

  return {
    virtualItems,
    topPadding,
    bottomPadding,
    totalHeight,
    handleScroll,
    scrollContainerRef,
  };
}

function OptionButton<V extends string = string>({
  option,
  selected,
  onSelect,
  renderOptionLabel,
  getOptionTitle,
  searchQuery,
}: {
  option: SelectOption<V>;
  selected: boolean;
  onSelect: (v: V) => void;
  renderOptionLabel?: (option: SelectOption<V>) => React.ReactNode;
  getOptionTitle?: (option: SelectOption<V>) => string | undefined;
  searchQuery?: string;
}) {
  const content = renderOptionLabel
    ? renderOptionLabel(option)
    : searchQuery
      ? highlightMatches(option.label, searchQuery)
      : option.label;

  return (
    <button
      key={(option.key as any) || (option.value as any)}
      type="button"
      title={getOptionTitle?.(option)}
      className={cn(
        "w-full",
        "rounded",
        "my-0.5",
        "px-3 py-2",
        "text-left",
        "hover:bg-gray-50",
        "focus:outline-none",
        "focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-inset",
        selected ? "bg-indigo-50 text-indigo-600 font-medium" : "text-gray-900",
        option.disabled
          ? "opacity-50 cursor-not-allowed hover:bg-transparent"
          : null,
      )}
      onClick={() => onSelect(option.value as V)}
      disabled={option.disabled}
    >
      {content}
    </button>
  );
}

export interface SearchableSelectProps<V extends string = string> {
  id?: string;
  options: SelectOption<V>[];
  className?: string;
  ariaLabel?: string;
  disabled?: boolean;
  placeholder?: string;
  // Controlled value
  value?: V;
  // Uncontrolled default value
  defaultValue?: V;
  // Change handler
  onChange?: (value: V | undefined, selectedOption?: SelectOption<V>) => void;
  searchPlaceholder?: string;
  disableSearch?: boolean;
  renderOptionLabel?: (option: SelectOption<V>) => React.ReactNode;
  getOptionTitle?: (option: SelectOption<V>) => string | undefined;
  getTriggerDisplayText?: (selected: V | undefined) => string;
  dropdownClassName?: string;
  disableVirtualization?: boolean;
}

export function SearchableSelect<V extends string = string>({
  id,
  options,
  className = "",
  ariaLabel = "combobox",
  disabled = false,
  placeholder = "Select",
  value,
  defaultValue,
  onChange,
  searchPlaceholder = "Search...",
  disableSearch = false,
  renderOptionLabel,
  getOptionTitle,
  getTriggerDisplayText,
  dropdownClassName,
  disableVirtualization = false,
}: SearchableSelectProps<V>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [search, setSearch] = useState("");

  const isControlled = value !== undefined;
  const [internalSelected, setInternalSelected] = useState<V | undefined>(
    defaultValue,
  );
  const effectiveSelected = isControlled ? value : internalSelected;

  const triggerClassName = cn(
    "relative",
    "border",
    "border-gray-300 text-black",
    "hover:border-gray-500",
    "focus:ring-gray-500 focus:border-gray-500",
    "active:text-black",
    "disabled:bg-black-50 disabled:border-black-100 disabled:text-black",
    "rounded-md shadow-sm",
    "px-3 py-2 pr-10",
    "w-full text-left",
    className,
  );

  const filteredOptions = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (s === "") return options;
    return options.filter((o) => o.label.toLowerCase().includes(s));
  }, [options, search]);

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    const next = e.relatedTarget as Node | null;
    if (!containerRef.current) return;
    if (!next || !containerRef.current.contains(next)) {
      setOpen(false);
    }
  };
  const handleMouseDown = () => {
    // Keep focus anchored on the container so blur won't fire when clicking non-focusable children
    containerRef.current?.focus();
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const clearSearch = () => {
    setSearch("");
    // Keep the user in the search input after clearing.
    searchInputRef.current?.focus();
  };

  const emitChange = (nextValue: V | undefined) => {
    const selectedOption = nextValue
      ? options.find((o) => o.value === nextValue)
      : undefined;
    onChange?.(nextValue, selectedOption);
    if (!isControlled) {
      setInternalSelected(nextValue);
    }
    // Close the dropdown after selection
    setOpen(false);
  };

  const onSelectOption = (v: V) => {
    emitChange(v);
  };

  const triggerLabel = useMemo(() => {
    if (getTriggerDisplayText) return getTriggerDisplayText(effectiveSelected);
    if (!effectiveSelected) return "";
    const found = options.find((o) => o.value === effectiveSelected);
    return found ? found.label : "";
  }, [effectiveSelected, options, getTriggerDisplayText]);

  const shouldVirtualize =
    !disableVirtualization && filteredOptions.length >= VIRTUALIZE_THRESHOLD;
  const {
    virtualItems,
    topPadding,
    bottomPadding,
    handleScroll,
    scrollContainerRef,
  } = useVirtualList(shouldVirtualize ? filteredOptions.length : 0);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [search]);

  const renderOptions = () => {
    if (filteredOptions.length === 0) {
      return <div className="text-sm text-gray-500 px-1 py-2">No results</div>;
    }

    if (shouldVirtualize) {
      return (
        <>
          {topPadding > 0 && <div style={{ height: topPadding }} aria-hidden />}
          {virtualItems.map(({ index }) => {
            const o = filteredOptions[index];
            const selected = effectiveSelected === o.value;
            return (
              <OptionButton
                key={(o.key as any) || (o.value as any)}
                option={o}
                selected={selected}
                onSelect={onSelectOption}
                renderOptionLabel={renderOptionLabel}
                getOptionTitle={getOptionTitle}
                searchQuery={search}
              />
            );
          })}
          {bottomPadding > 0 && (
            <div style={{ height: bottomPadding }} aria-hidden />
          )}
        </>
      );
    }

    return filteredOptions.map((o) => {
      const selected = effectiveSelected === o.value;
      return (
        <OptionButton
          key={(o.key as any) || (o.value as any)}
          option={o}
          selected={selected}
          onSelect={onSelectOption}
          renderOptionLabel={renderOptionLabel}
          getOptionTitle={getOptionTitle}
          searchQuery={search}
        />
      );
    });
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      tabIndex={-1}
      onBlur={handleBlur}
      onMouseDown={handleMouseDown}
      onKeyDown={handleKeyDown}
    >
      <button
        id={id}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerClassName}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <span className="block truncate">{triggerLabel || placeholder}</span>
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-700">
          <IconChevronDown variant="sm" />
        </span>
      </button>

      {open ? (
        <div
          role="listbox"
          className={cn(
            "absolute z-10 mt-1 min-w-full",
            "bg-white border border-gray-200 rounded-md shadow-lg",
            "p-2",
            dropdownClassName,
          )}
        >
          {!disableSearch && (
            <>
              <div className="relative">
                <IconSearch
                  className="absolute top-[10px] left-[10px]"
                  color="#595E63"
                  variant="sm"
                />
                <Input
                  type="text"
                  placeholder={searchPlaceholder}
                  value={search}
                  onChange={(e) => setSearch(e.currentTarget.value)}
                  className="pl-9 pr-9 w-full"
                  autoFocus
                  ref={searchInputRef}
                />
                {search.trim() !== "" ? (
                  <button
                    type="button"
                    aria-label="Clear search"
                    className={cn(
                      "absolute",
                      "top-1/2 -translate-y-1/2 right-[10px]",
                      "text-gray-500 hover:text-gray-700",
                      "focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-500 focus-visible:ring-inset rounded",
                    )}
                    onClick={clearSearch}
                  >
                    <IconX variant="sm" color="#6B7280" />
                  </button>
                ) : null}
              </div>
              <div className="h-px bg-gray-200 my-2" />
            </>
          )}
          <div
            ref={scrollContainerRef}
            onScroll={shouldVirtualize ? handleScroll : undefined}
            className="max-h-64 overflow-auto pr-1 pl-2 py-1"
          >
            {renderOptions()}
          </div>
        </div>
      ) : null}
    </div>
  );
}
